import {cx as addStyles, css} from '@emotion/css';
import {
  CustomerPortalModalCashOut,
  CustomerPortalModalGiftCard,
  CustomerPortalModalGiftCardDetails,
  FlatPickup,
} from '@remoov/remoov-components';
import {History} from 'history';
import {assocPath, last, mergeDeepLeft, prop, sortBy} from 'ramda';
import React, {useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import {
  BLUEGROUP_ID,
  Constants,
  CustomerItemStatus,
  CustomerPortalCheckFormModal,
  CustomerPortalModalCheckNext,
  CustomerPortalModalConfirmed,
  CustomerPortalModalPaypal,
  CustomerPortalModalPaypalDetails,
  DonationReceipt,
  EMPTY_OWNER,
  EMPTY_SUSTAINABILITY_IMPACT,
  Loading,
  Owner,
  SustainabilityImpact,
  Trip,
  apiCall,
  arrayToOptions,
  coerceToError,
  hostEndpoint,
  isMyCollectionAvailable,
  pickupFullLocation,
  remoovNodeEndpoint,
  renderDate,
  theme,
  url,
  validateOwnerCashout,
} from '../../Common';
import {
  CashOutCard,
  Footer,
  IntroCard,
  ItemSummaryCard,
  ListCard,
  Menu,
  SustainabilityImpactCard,
} from '../../Components';

const {
  State: States,
  NewSiteUrlJs: {YOUR_QUOTE_URL},
  NewCustomerPortalUrl: {BETA_ITEMS_URL, BETA_DASHBOARD_URL, BETA_EXPIRED_ITEMS_URL},
  PaymentType: {PAYPAL, CHECK, GIFT_CARD},
} = Constants;
const {PAID, PENDING_SALE, SOLD, CLAIMED, DONATED, FREE, RECYCLED, RETURNED, FOR_SALE, PROCESSING} =
  CustomerItemStatus;

export type CustomerPortalDashboardState = {
  customerName: string;
  pendingBalance: boolean;
  expiredItemsCount: number;
  owner: Owner;
  pickups: FlatPickup[];
  requestPublicReview: boolean;
  counts: Record<keyof typeof CustomerItemStatus, number> | null;
  cashOutModal: boolean;
  checkModal: boolean;
  paypalModal: boolean;
  giftCardModal: boolean;
  paypalDetailsModal: boolean;
  giftCardDetailsModal: boolean;
  checkDetailsModal: boolean;
  showModal: boolean;
  cashoutSubmitted: boolean;
  isLoading: boolean;
  pageLoading: boolean;
  donationReceipts: DonationReceipt[];
  sustainabilityImpact: SustainabilityImpact;
  enableGiftCard: boolean;
};
export type CustomerPortalDashboardProps = {
  history: History;
  location: {query: Record<string, string>};
};
export const CustomerPortalDashboard = ({
  history,
  location: {query},
}: CustomerPortalDashboardProps) => {
  const [state, setState] = useState<CustomerPortalDashboardState>({
    customerName: '',
    pendingBalance: false,
    expiredItemsCount: 0,
    owner: EMPTY_OWNER,
    pickups: [],
    requestPublicReview: false,
    counts: null,
    cashOutModal: false,
    checkModal: false,
    paypalModal: false,
    giftCardModal: false,
    showModal: false,
    paypalDetailsModal: false,
    giftCardDetailsModal: false,
    checkDetailsModal: false,
    cashoutSubmitted: false,
    isLoading: false,
    pageLoading: true,
    donationReceipts: [],
    sustainabilityImpact: EMPTY_SUSTAINABILITY_IMPACT,
    enableGiftCard: false,
  });

  const reload = async () => {
    const {owner, ...rest} = await apiCall('customerPortalDashboardView');
    const isBlueground = owner.id === BLUEGROUP_ID;
    setState(
      //@ts-ignore
      mergeDeepLeft({
        ...rest,
        isLoading: false,
        pageLoading: false,
        enableGiftCard:
          new URLSearchParams(window.location.search).get('enableGiftCard') === 'true',
        owner: {
          ...owner,
          payment_preference: isBlueground ? 'Check' : owner.payment_preference,
        },
      }),
    );
  };

  useEffect(() => {
    reload();
  }, []);

  useEffect(() => {
    const handleKeyUp = ({key}: KeyboardEvent) => {
      if (key === 'Escape')
        setState(
          mergeDeepLeft({
            cashOutModal: false,
            checkModal: false,
            paypalModal: false,
            giftCardModal: false,
            paypalDetailsModal: false,
            giftCardDetailsModal: false,
            checkDetailsModal: false,
            cashoutSubmitted: false,
          }),
        );
    };
    window.addEventListener('keyup', handleKeyUp);
    return () => window.removeEventListener('keyup', handleKeyUp);
  }, []);

  const {
    customerName,
    expiredItemsCount,
    owner,
    owner: {location},
    pickups,
    counts,
    cashOutModal,
    checkModal,
    paypalModal,
    giftCardModal,
    paypalDetailsModal,
    giftCardDetailsModal,
    checkDetailsModal,
    cashoutSubmitted,
    isLoading,
    pageLoading,
    donationReceipts,
    sustainabilityImpact,
    requestPublicReview,
    enableGiftCard,
  } = state;
  const market = last(pickups)?.market;
  const encryptedId = last(pickups)?.encrypted_id;

  const updatePaymentPreference = (payment_preference: string) => {
    return () =>
      setState(
        mergeDeepLeft({
          owner: {payment_preference},
          cashOutModal: false,
          paypalModal: payment_preference === PAYPAL,
          checkModal: payment_preference === CHECK,
          giftCardModal: payment_preference === GIFT_CARD,
        }),
      );
  };
  const cashOut = async () => {
    try {
      setState(mergeDeepLeft({isLoading: true}));
      await apiCall('cashOut', owner);
      setState(
        mergeDeepLeft({
          cashoutSubmitted: true,
          isLoading: false,
          paypalModal: false,
          giftCardModal: false,
          paypalDetailsModal: false,
          checkModal: false,
          checkDetailsModal: false,
          giftCardDetailsModal: false,
        }),
      );
      await reload();
    } catch (maybeError) {
      const error = coerceToError(maybeError);
      setState(mergeDeepLeft({isLoading: false}));
      alert(error.message || 'Server error. Please try again.');
    }
  };
  const paypalModalSubmit = () => {
    setState(mergeDeepLeft({paypalModal: false, paypalDetailsModal: true}));
  };
  const checkModalSubmit = () => {
    setState(mergeDeepLeft({checkModal: false, checkDetailsModal: true}));
  };
  const giftCardDetailsModalSubmit = () => {
    setState(mergeDeepLeft({giftCardDetailsModal: true, giftCardModal: false}));
  };
  const errors = validateOwnerCashout(owner);
  const states = arrayToOptions(States);
  const cashoutInvalid = Object.values(errors).some(Boolean);
  const totalAmountCashOut = owner?.balance_due || 0;
  const pickupsHistory = pickups
    .map((pickup): [FlatPickup, Trip[]] => [
      pickup,
      sortBy(
        // @ts-ignore already filtered
        prop('scheduled_start'),
        pickup.trips.filter(t => !t.deleted_at && t.actual_finish && t.scheduled_start),
      ),
    ])
    .map(([pickup, trips], index) => (
      <Link
        key={index}
        to={{
          pathname: BETA_ITEMS_URL,
          search: `?pickupId=${String(pickup.id)}`,
        }}
      >
        <span>
          {trips
            .map(prop('scheduled_start'))
            .filter(Boolean)
            .map(s => renderDate(s))
            .join(', ')}{' '}
          - {pickupFullLocation(pickup)}
        </span>
      </Link>
    ));
  const donationReceiptLinks = donationReceipts.map(({timestamp, receipt_url}, index) => {
    if (!receipt_url) return renderDate(timestamp);
    return (
      <a href={receipt_url} key={index} target='_blank' rel='noreferrer'>
        {renderDate(timestamp)}
      </a>
    );
  });

  return (
    <Container>
      <Menu
        dashboardLink={() => history.push(BETA_DASHBOARD_URL)}
        myItemsLink={() => history.push(BETA_ITEMS_URL)}
        newPickupLink={() => {
          if (owner.id)
            // @ts-ignore window.location
            window.location = url([hostEndpoint(), YOUR_QUOTE_URL.slice(1)], {
              email: owner?.email,
            });
          history.push(BETA_DASHBOARD_URL);
        }}
        logoutLink={async () => {
          const response = await fetch(`${remoovNodeEndpoint()}/sign-out`, {
            method: 'POST',
            headers: {'Content-type': 'application/json'},
            credentials: 'include',
            redirect: 'follow',
          });
          const {url} = await response.json();
          // @ts-ignore
          window.location = url;
        }}
      />
      {pageLoading ? (
        <div style={{margin: '100px 0'}}>
          <Loading width={100} height={100} type='spinningBubbles' color={theme.colors.darkBlue} />
        </div>
      ) : (
        <Content>
          <ContentGrid>
            <IntroCard
              customerName={customerName}
              expiredItems={expiredItemsCount > 0}
              expiredItemsLink={() => history.push(BETA_EXPIRED_ITEMS_URL)}
              requestPublicReview={requestPublicReview}
              market={market}
              encryptedId={encryptedId}
              ownerId={owner.id}
              showMyCollection={
                (!!counts && !!pickups && isMyCollectionAvailable(counts, pickups)) ||
                query['my-collection'] === 'true'
              }
            />
            <CashOutCard
              balanceOwedPrice={owner?.balance_due}
              previousPaidPrice={owner?.amount_paid}
              openModal={() => setState(mergeDeepLeft({cashOutModal: true}))}
            />
            <ListCard title='Pickup History' items={pickupsHistory} emptyMessage='No pickups' />
            <ListCard
              title='Donation Receipts'
              items={donationReceiptLinks}
              emptyMessage='No donations'
            />
          </ContentGrid>
          {counts && (
            <ItemSummaryCard
              collected={
                (counts[PAID] || 0) +
                (counts[SOLD] || 0) +
                (counts[PENDING_SALE] || 0) +
                (counts[CLAIMED] || 0) +
                (counts[DONATED] || 0) +
                (counts[FREE] || 0) +
                (counts[RECYCLED] || 0) +
                (counts[RETURNED] || 0) +
                (counts[FOR_SALE] || 0) +
                (counts[PROCESSING] || 0) +
                (counts[CustomerItemStatus.AUCTIONING] || 0) +
                (counts[CustomerItemStatus.AUCTIONED] || 0)
              }
              forSale={
                (counts[FOR_SALE] || 0) +
                (counts[CLAIMED] || 0) +
                (counts[PENDING_SALE] || 0) +
                (counts[CustomerItemStatus.AUCTIONING] || 0)
              }
              sold={(counts[SOLD] || 0) + (counts[PAID] || 0)}
              auctioned={counts[CustomerItemStatus.AUCTIONED] || 0}
              donated={(counts[DONATED] || 0) + (counts[FREE] || 0)}
              recycled={counts[RECYCLED] || 0}
              className={css({marginBottom: theme.spacings.small})}
            />
          )}
          <SustainabilityImpactCard sustainabilityImpact={sustainabilityImpact} />
        </Content>
      )}
      {cashOutModal && (
        <CustomerPortalModalCashOut
          isOpen={cashOutModal}
          setIsOpen={() => setState(mergeDeepLeft({cashOutModal: !cashOutModal}))}
          value={totalAmountCashOut}
          onClickCheck={updatePaymentPreference(CHECK)}
          onClickPaypal={updatePaymentPreference(PAYPAL)}
          onClickGiftCard={enableGiftCard ? updatePaymentPreference(GIFT_CARD) : null}
        />
      )}
      {paypalModal && (
        <CustomerPortalModalPaypal
          isOpen={paypalModal}
          setIsOpen={() => setState(mergeDeepLeft({paypalModal: !paypalModal}))}
          email={owner.paypal_email || ''}
          setEmail={value => setState(mergeDeepLeft({owner: {paypal_email: value}}))}
          nextClick={paypalModalSubmit}
        />
      )}
      {giftCardModal && (
        <CustomerPortalModalGiftCard
          isOpen={giftCardModal}
          setIsOpen={() => setState(mergeDeepLeft({giftCardModal: !giftCardModal}))}
          amount={totalAmountCashOut}
          setAmount={value => setState(assocPath(['owner', 'balance_due'], value))}
          email={owner.gift_card_email || ''}
          setEmail={value => setState(assocPath(['owner', 'gift_card_email'], value))}
          nextClick={giftCardDetailsModalSubmit}
        />
      )}
      {giftCardDetailsModal && (
        <CustomerPortalModalGiftCardDetails
          isOpen={giftCardDetailsModal}
          setIsOpen={() => setState(mergeDeepLeft({giftCardDetailsModal: !giftCardDetailsModal}))}
          value={totalAmountCashOut}
          email={owner.gift_card_email || ''}
          cashOut={cashOut}
          disabled={cashoutInvalid || isLoading}
        />
      )}
      {paypalDetailsModal && (
        <CustomerPortalModalPaypalDetails
          isOpen={paypalDetailsModal}
          setIsOpen={() => setState(mergeDeepLeft({paypalDetailsModal: !paypalDetailsModal}))}
          value={totalAmountCashOut}
          email={owner.paypal_email || ''}
          cashOut={cashOut}
          disabled={cashoutInvalid || isLoading}
        />
      )}
      {checkModal && (
        <CustomerPortalCheckFormModal
          isOpen={checkModal}
          setModal={() => setState(mergeDeepLeft({checkModal: !checkModal}))}
          inputs={{
            name: owner.payment_name || '',
            setName: value => setState(assocPath(['owner', 'payment_name'], value)),
            address: location?.address || '',
            setAddress: value => setState(assocPath(['owner', 'location', 'address'], value)),
            addressLine2: location?.address_line_2 || '',
            setAddressLine2: value =>
              setState(assocPath(['owner', 'location', 'address_line_2'], value)),
            city: location?.city || '',
            setCity: value => setState(assocPath(['owner', 'location', 'city'], value)),
            state: location?.state || '',
            setState: state => setState(assocPath(['owner', 'location', 'state'], state)),
            zipcode: location?.zipcode || '',
            setZipcode: value => setState(assocPath(['owner', 'location', 'zipcode'], value)),
          }}
          states={states}
          onClickNext={checkModalSubmit}
        />
      )}
      {checkDetailsModal && (
        <CustomerPortalModalCheckNext
          isOpen={checkDetailsModal}
          setIsOpen={() => setState(mergeDeepLeft({checkDetailsModal: !checkDetailsModal}))}
          address={location?.address || ''}
          address2={location?.address_line_2 || ''}
          name={owner.payment_name || ''}
          city={location?.city || ''}
          state={location?.state || ''}
          zipcode={location?.zipcode || ''}
          value={totalAmountCashOut}
          cashOut={cashOut}
          disabled={cashoutInvalid || isLoading}
        />
      )}
      {cashoutSubmitted && (
        <CustomerPortalModalConfirmed
          isOpen={cashoutSubmitted}
          setIsOpen={() => setState(mergeDeepLeft({cashoutSubmitted: !cashoutSubmitted}))}
        >
          {'Your withdraw request has been completed!'}
        </CustomerPortalModalConfirmed>
      )}{' '}
      <Footer market={pickups[0]?.market} />
    </Container>
  );
};
const mediaQueries = {
  small: '@media (max-width: 450px)',
  mobile1: '@media (max-width: 550px)',
};
const centralizedFlexColumn = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
});
function Container({className, ...rest}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={addStyles(
        centralizedFlexColumn,
        css({
          width: '100%',
          height: '100%',
          background: theme.colors.lightBlue,
          position: 'relative',
        }),
        className,
      )}
      {...rest}
    />
  );
}
function Content({className, ...rest}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={addStyles(
        centralizedFlexColumn,
        css({
          width: '100%',
          maxWidth: '1200px',
          margin: `calc(${theme.spacings.large}*2) 0`,
          background: 'transparent',
          [mediaQueries.small]: {padding: ` 0 ${theme.spacings.xsmall}`},
        }),
        className,
      )}
      {...rest}
    />
  );
}
function ContentGrid({className, ...rest}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={addStyles(
        css({
          width: '100%',
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: theme.spacings.xsmall,
          marginBottom: theme.spacings.small,
          [mediaQueries.mobile1]: {
            gridTemplateColumns: '1fr',
            gap: theme.spacings.large,
          },
        }),
        className,
      )}
      {...rest}
    />
  );
}
